import React from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import Option from "../../models/Option";

const selectStyles = {
    control: (styles: any, state: any) => ({
        ...styles,
        backgroundColor: 'var(--cds-surface-input)',
        border: state.isFocused
            ? '1px solid transparent'
            : '1px solid var(--cds-border-subtle)',
        borderRadius: '6px',
        boxShadow: state.isFocused
            ? '0 0 0 1.5px var(--cds-accent), 0 0 16px rgba(var(--cds-accent-rgb), 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.03)'
            : 'none',
        fontSize: '.8rem',
        minHeight: '38px',
        transition: 'border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        "&:hover": {
            borderColor: state.isFocused ? 'transparent' : 'rgba(var(--cds-accent-rgb), 0.3)',
            backgroundColor: 'var(--cds-surface-input-hover)',
        },
    }),
    menu: (provided: any) => ({
        ...provided,
        backgroundColor: 'var(--cds-surface-input-focus)',
        border: '1px solid var(--cds-border-subtle)',
        borderRadius: '6px',
        color: 'var(--cds-text-secondary)',
        fontSize: '.8rem',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
        overflow: 'hidden',
    }),
    option: (styles: any, meta: any) => {
        const { data, isDisabled/*, isFocused, isSelected*/ } = meta;
        const color = data.color;
        return {
            ...styles,
            backgroundColor: 'transparent',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            color: color || 'var(--cds-text-secondary)',
            transition: 'background 0.15s ease, color 0.15s ease',
            "&:hover": {
                backgroundColor: 'rgba(var(--cds-accent-rgb), 0.12)',
                color: 'var(--cds-text-primary)',
            },
            "&:active": {
                backgroundColor: 'var(--cds-accent-alpha-20)',
                color: 'var(--cds-text-primary)',
            },
        };
    },
    singleValue: (provided: any, state: any) => ({
        ...provided,
        opacity: state.isDisabled ? 0.45 : 1,
        transition: 'opacity 250ms',
        color: 'var(--cds-text-secondary)',
    }),
    indicatorSeparator: (provided: any) => ({
        ...provided,
        backgroundColor: 'var(--cds-border-subtle)',
    }),
    dropdownIndicator: (provided: any, state: any) => ({
        ...provided,
        color: state.isFocused ? 'var(--cds-accent)' : 'var(--cds-text-muted)',
        transition: 'color 0.25s ease',
        '&:hover': { color: 'var(--cds-accent)' },
    }),
    clearIndicator: (provided: any) => ({
        ...provided,
        color: 'var(--cds-text-muted)',
        '&:hover': { color: 'var(--cds-accent-red)' },
    }),
    input: (defaultStyles: any) => ({
        ...defaultStyles,
        color: 'var(--cds-text-secondary)',
    }),
    multiValue: (styles: any) => ({
        ...styles,
        backgroundColor: 'var(--cds-accent-alpha-18)',
        borderRadius: '4px',
    }),
    multiValueLabel: (styles: any) => ({
        ...styles,
        backgroundColor: 'transparent',
        color: 'var(--cds-accent)',
        fontWeight: '500',
        fontSize: '0.75rem',
    }),
    multiValueRemove: (styles: any) => ({
        ...styles,
        color: 'var(--cds-accent)',
        borderRadius: '0 4px 4px 0',
        '&:hover': {
            backgroundColor: 'var(--cds-danger-alpha-10)',
            color: 'var(--cds-accent-red)',
            cursor: 'pointer',
        },
    }),
};

interface CustomSelectProps {
    options: Option[];
    value: any;
    onChange: any;
    widthDefaultOption?: boolean;
    config?: any;
}

const CustomSelect: React.FC<CustomSelectProps> = (props: CustomSelectProps) => {

    const [t] = useTranslation();
    let options: Option[] = props.options ?? [];
    
    const widthDefaultOption = props.widthDefaultOption ?? true;

    let myOtions: Option[] = widthDefaultOption ? [
        {label: "-", value: null},
        ...options
    ] : [...options];

    return(
        <Select
            {...props}
            isMulti={props.config?.isMulti ?? false}
            options={myOtions}
            styles={selectStyles}
            noOptionsMessage={() => t('No Result')}
            placeholder="-"
        />
    );
}

export default React.memo(CustomSelect);
